/**
 * @module app.ext.iqc
 */

import { autoinject, bindable } from 'aurelia-framework';
import { IOpenIQCParameters, IqcManager } from 'app/ext/iqc/iqc-manager';
import { AuthPageContext } from 'app/um/lib/auth-page-context';
import { Api, apiCall } from 'app/shared/lib/essette-api';
import { IApiQueryResults, IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { AuthService, IAuthMedicalReviewHistoryFilters } from 'app/um/services/auth-service';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { AuthIQCMedicalReviewEvent } from 'app/um/lib/events';
import { IExtIqcSettings } from 'app/ext/iqc/ext-iqc';
import { localSort } from 'app/shared/lib/sorting';
import { deepClone } from 'app/shared/lib/utilities';
import * as UM from 'app/um/models/essette-api';

@autoinject()
export class IqcMedicalReviewsGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable({ changeHandler: 'reload' })
    public service?: UM.AuthService;

    @bindable({ changeHandler: 'reload' })
    public diagnosis?: UM.AuthDiagnosis;

    @bindable
    protected authId?: string;

    @bindable
    protected readonly = false;

    @bindable
    protected showPager = true;

    @bindable
    protected isSaveDetail = false;

    @bindable
    protected extIqcSettings: IExtIqcSettings = {
        allowOpen: true,
        showOpenBookView: false,
        showOpenReviewSummaryPdf: true,
        allowReviewCopy: true
    };

    protected medicalReviewHistory: UM.AuthMedicalReviewHistory[] = [];

    private medicalReviewPublished: Subscription | null = null;

    private sorting = new Sorting();
    private defaultSorting = new Sorting();

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        protected readonly api: Api,
        private readonly authService: AuthService,
        protected iqc: IqcManager,
        private context: AuthPageContext,
        private readonly events: EventAggregator,
        ...rest: any[]) {
        super(...rest);
        this.defaultSorting.add({ reference: 'reviewLastChangedDateTime', direction: SortDirection.Descending });
    }

    protected bind(): void {
        this.reload();
        // If medical review grid is coming from save diagnosis or service component, do not subscribe to event.
        // Will be taken care of on the diagnosis or service change handler instead
        if(!this.isSaveDetail) {
            this.medicalReviewPublished = this.events.subscribe(AuthIQCMedicalReviewEvent, () => {
                this.reload();
            });
        }
    }

    protected unbind() {
        if(this.medicalReviewPublished) this.medicalReviewPublished.dispose();
    }

    ////****************** Events ***************************************************////
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        localSort(this.medicalReviewHistory, sorting.entries.length ? sorting : this.defaultSorting);
    }

    ////****************** Functions ************************************************////
    protected async reload(): Promise<void> {
        this.medicalReviewHistory.splice(0);
        await this.loadMedicalReviewHistory();
    }

    protected open(): void {
        if(!this.service && !this.diagnosis) return;
        const openIqcParameters: IOpenIQCParameters = {
            auth: this.context.auth,
            diagnosis: this.diagnosis,
            services: this.service ? [this.service] : undefined
        };
        this.iqc.open(openIqcParameters);
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadMedicalReviewHistory(): Promise<void> {
        if(!this.service?.id && !this.diagnosis?.id && !this.authId) return;

        const options: Partial<IQueryLoadOptions> = {
            all: true,
            sorting: this.sorting
        };

        const includes: string[] = [
            'medicalReview'
        ];

        const fields: string[] = [
            'audit.end.id',
            'complete',
            'criteriaStatus',
            'criteriaVersion',
            'currentStatus',
            'identifier.external',
            'reviewCreatedByUser',
            'reviewCreatedDateTime',
            'reviewLastChangedByUser',
            'reviewLastChangedDateTime',
            'productDescription',
            'subsetDescription'
        ];

        const filters: IAuthMedicalReviewHistoryFilters = {
            authId: this.authId,
            authServiceId: this.service?.id,
            authDiagnosisId: this.diagnosis?.id
        };

        try {
            this.state.beginLoad();

            let response: IApiQueryResults<UM.AuthMedicalReviewHistory>;

            response = await this.authService.loadAuthMedicalReviewHistory(filters, options, includes, fields);

            const unfilteredMedicalReviewHistory = response.results;

            // Get distinct Medical Reviews and the last audit status
            unfilteredMedicalReviewHistory.forEach(reviewHistoryLog => {
                let existingReviewIndex = this.medicalReviewHistory.findIndex(r => r.identifier?.external === reviewHistoryLog.identifier?.external);

                const insertHistoryLog = {
                    ...reviewHistoryLog,
                    medicalReview: {
                        ...reviewHistoryLog.medicalReview,
                        complete: reviewHistoryLog.complete,
                        identifier: {
                            external: reviewHistoryLog.identifier?.external
                        }
                    }
                };

                // If already in medicalReviewHistory, check if reviewLastChangedDateTime is later than the previous history log.
                if(existingReviewIndex > -1) {
                    if((reviewHistoryLog.reviewLastChangedDateTime ? Date.parse(reviewHistoryLog.reviewLastChangedDateTime) : 0) > Date.parse(this.medicalReviewHistory[existingReviewIndex].reviewLastChangedDateTime!)) {
                        this.medicalReviewHistory[existingReviewIndex] = deepClone(insertHistoryLog);
                    }
                }
                else {
                    this.medicalReviewHistory.push(insertHistoryLog);
                }
            });
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
